import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RefreshActions } from '../../state/refresh'
import { useRequests } from '../../requests/useRequests'
import { TaskBoardType } from '../../types/task-board'
import { State } from '../../types/state'
import { SideMenu } from '../Header/SiteMenu'
import { TaskBoardHeader } from './TaskBoardHeader'
import { TaskBoardContent } from './TaskBoardContent'
import { TaskBoardModals } from './TaskBoardModals'
import AppNavBar from '../Header/AppNavBar'
import { TaskBoardIcons } from './TaskBoardIcons'

const TaskBoard: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const requestMaker = useRequests()
  const { package: userPackage, packageExpiredAt, packageUsersNumber } = useSelector((state: State) => state.user)
  const userState = useSelector((state: State) => state.user)

  // Stan aplikacji
  const [board, setBoard] = useState<TaskBoardType | null>(null)
  const [loading, setLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showAddColumnModal, setShowAddColumnModal] = useState(false)
  const [showAddCardModal, setShowAddCardModal] = useState<string | null>(null)
  const [showCardModal, setShowCardModal] = useState<{ columnId: string, cardId: string } | null>(null)
  const [viewMode, setViewMode] = useState<'columns' | 'table'>('columns')
  const [filterMode, setFilterMode] = useState<'all' | 'assigned'>('all')
  const [showPriorityModal, setShowPriorityModal] = useState<{ columnId: string, cardId: string } | null>(null)
  const [showAssigneeModal, setShowAssigneeModal] = useState<{ columnId: string, cardId: string } | null>(null)
  const [showDependencyModal, setShowDependencyModal] = useState<{ columnId: string, cardId: string } | null>(null)
  const [showDueDateModal, setShowDueDateModal] = useState<{ columnId: string, cardId: string } | null>(null)
  const [isPredefineMode, setIsPredefineMode] = useState(false)
  const [selectedStage, setSelectedStage] = useState<string>('')
  const [selectedTask, setSelectedTask] = useState<string>('')

  // Dane formularzy
  const [inviteEmail, setInviteEmail] = useState('')
  const [newColumnTitle, setNewColumnTitle] = useState('')
  const [newCard, setNewCard] = useState<{ title: string, description: string, priority: number }>({ title: '', description: '', priority: 0 })

  useEffect(() => {
    fetchBoard()
  }, [id])

  const fetchBoard = async () => {
    try {
      if (id) {
        const response = await requestMaker.getTaskBoard(id)
        setBoard(response.data)
        setLoading(false)
      }
    } catch (error) {
      console.error('Błąd podczas pobierania tablicy zadań', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się pobrać tablicy zadań'
        })
      )
      setLoading(false)
    }
  }

  const handleInviteUser = async () => {
    if (!inviteEmail.trim() || (board == null)) return

    // Sprawdzamy najpierw, czy nie przekroczono limitu
    if (packageUsersNumber && board.members.length >= packageUsersNumber) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Osiągnięto limit użytkowników dostępny w Twoim pakiecie'
        })
      )
      return
    }

    try {
      await requestMaker.inviteToTaskBoard(board.id, inviteEmail)
      setInviteEmail('')
      setShowInviteModal(false)
      fetchBoard() // Odśwież dane, aby pokazać nowego członka

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Zaproszenie zostało wysłane'
        })
      )
    } catch (error) {
      console.error('Błąd podczas zapraszania użytkownika', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zaprosić użytkownika'
        })
      )
    }
  }

  const handleAddColumn = async () => {
    if (!newColumnTitle.trim() || (board == null)) return

    try {
      await requestMaker.createTaskColumn(board.id, newColumnTitle)
      setNewColumnTitle('')
      setShowAddColumnModal(false)
      fetchBoard() // Odśwież dane, aby pokazać nową kolumnę

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Kolumna została dodana'
        })
      )
    } catch (error) {
      console.error('Błąd podczas dodawania kolumny', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się dodać kolumny'
        })
      )
    }
  }

  const handleAddCard = async (columnId: string) => {
    if (!newCard.title.trim() || (board == null)) return

    try {
      await requestMaker.createTaskCard(board.id, {
        title: newCard.title,
        description: newCard.description,
        columnId,
        priority: newCard.priority || 0
      })
      setNewCard({ title: '', description: '', priority: 0 })
      setShowAddCardModal(null)
      fetchBoard() // Odśwież dane, aby pokazać nową kartę

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Karta została dodana'
        })
      )
    } catch (error) {
      console.error('Błąd podczas dodawania karty', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się dodać karty'
        })
      )
    }
  }

  const handleDeleteCard = async (columnId: string, cardId: string) => {
    if (board == null) return

    try {
      await requestMaker.deleteTaskCard(board.id, cardId)
      setShowCardModal(null)
      fetchBoard() // Odśwież dane, aby usunąć kartę

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Karta została usunięta'
        })
      )
    } catch (error) {
      console.error('Błąd podczas usuwania karty', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się usunąć karty'
        })
      )
    }
  }

  const handleAssignUser = async (cardId: string, userId: string | null) => {
    if (board == null) return

    try {
      await requestMaker.assignTaskCard(board.id, cardId, userId)
      fetchBoard() // Odśwież dane, aby pokazać zaktualizowaną kartę

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Użytkownik został przypisany do karty'
        })
      )
    } catch (error) {
      console.error('Błąd podczas przypisywania użytkownika', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się przypisać użytkownika'
        })
      )
    }
  }

  const handleAddComment = async (cardId: string, commentContent: string) => {
    if (board == null || !commentContent.trim()) return

    try {
      await requestMaker.addTaskComment(board.id, cardId, { content: commentContent })
      fetchBoard() // Odśwież dane, aby pokazać nowy komentarz

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Komentarz został dodany'
        })
      )
    } catch (error) {
      console.error('Błąd podczas dodawania komentarza', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się dodać komentarza'
        })
      )
    }
  }

  const handleDeleteColumn = async (columnId: string) => {
    if (board == null) return

    try {
      await requestMaker.deleteTaskColumn(board.id, columnId)
      fetchBoard() // Odśwież dane, aby usunąć kolumnę

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Kolumna została usunięta'
        })
      )
    } catch (error) {
      console.error('Błąd podczas usuwania kolumny', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się usunąć kolumny'
        })
      )
    }
  }

  const handleUpdateCardDescription = async (cardId: string, description: string) => {
    if (board == null) return

    try {
      await requestMaker.updateTaskCard(board.id, cardId, { description })
      fetchBoard() // Odśwież dane, aby pokazać zaktualizowaną kartę

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Opis zadania został zaktualizowany'
        })
      )
    } catch (error) {
      console.error('Błąd podczas aktualizacji opisu karty', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zaktualizować opisu zadania'
        })
      )
    }
  }

  const handleMoveCard = async (cardId: string, sourceColumnId: string, destinationColumnId: string) => {
    if (board == null || sourceColumnId === destinationColumnId) return

    try {
      // Znajdujemy kolumny źródłową i docelową
      const sourceColumn = board.columns.find(col => col.id === sourceColumnId)
      const destColumn = board.columns.find(col => col.id === destinationColumnId)

      if ((sourceColumn == null) || (destColumn == null)) return

      // Znajdujemy kartę i jej indeks w kolumnie źródłowej
      const sourceCardIndex = sourceColumn.cards.findIndex(c => c.id === cardId)
      if (sourceCardIndex === -1) return

      // Tworzymy aktualizację UI przed wykonaniem operacji na serwerze
      const tempBoard = JSON.parse(JSON.stringify(board))
      const tempSourceColumn = tempBoard.columns.find((col: { id: string }) => col.id === sourceColumnId)
      const tempDestColumn = tempBoard.columns.find((col: { id: string }) => col.id === destinationColumnId)

      if (tempSourceColumn && tempDestColumn) {
        // Przenosimy kartę między kolumnami w interfejsie
        const [removedCard] = tempSourceColumn.cards.splice(sourceCardIndex, 1)
        tempDestColumn.cards.push(removedCard)
        setBoard(tempBoard)
      }

      // Zamykamy modal przed wywołaniem API
      setShowCardModal(null)

      const targetPosition = destColumn.cards.reduce((acc, curr) => {
        if (acc <= curr.position) {
          return curr.position + 1
        }
        return acc
      }, 0)

      try {
        await requestMaker.reorderTaskCards(board.id, [
          {
            id: cardId,
            columnId: destinationColumnId,
            position: targetPosition
          }
        ])

        // Odświeżamy dane po udanej aktualizacji
        fetchBoard()

        dispatch(
          RefreshActions.setToastMessage({
            message: 'Zadanie zostało przeniesione do innej kolumny'
          })
        )
      } catch (error) {
        console.error('Błąd podczas przenoszenia karty', error)
        // Przywracamy poprzedni stan w przypadku błędu
        fetchBoard()

        dispatch(
          RefreshActions.setToastMessage({
            message: 'Nie udało się przenieść zadania'
          })
        )
      }
    } catch (error) {
      console.error('Błąd podczas przygotowania przeniesienia karty', error)
    }
  }

  const handleChangeCardPosition = async (cardId: string, columnId: string, direction: 'up' | 'down') => {
    if (board == null) return

    try {
      // Znajdujemy kolumnę
      const column = board.columns.find(col => col.id === columnId)
      if (column == null) return

      // Znajdujemy kartę i jej indeks w kolumnie
      const cardIndex = column.cards.findIndex(c => c.id === cardId)
      if (cardIndex === -1) return

      // Sprawdzamy, czy możemy przesunąć kartę w danym kierunku
      if (direction === 'up' && cardIndex === 0) return
      if (direction === 'down' && cardIndex === column.cards.length - 1) return

      // Obliczamy nową pozycję
      const newIndex = direction === 'up' ? cardIndex - 1 : cardIndex + 1

      // Identyfikujemy kartę, z którą zamieniamy pozycję
      const otherCardId = column.cards[newIndex].id

      // Tworzymy unikalny interfejs użytkownika dla operacji w toku
      const tempBoard = JSON.parse(JSON.stringify(board))
      const tempColumn = tempBoard.columns.find((col: { id: string }) => col.id === columnId)
      if (tempColumn) {
        // Zamieniamy karty miejscami w UI
        const temp = tempColumn.cards[cardIndex]
        tempColumn.cards[cardIndex] = tempColumn.cards[newIndex]
        tempColumn.cards[newIndex] = temp
        setBoard(tempBoard)
      }

      try {
        // Używamy reorderTaskCards, aby zaktualizować obie karty jednocześnie
        // Przypisujemy im tymczasowo bardzo odległe pozycje, aby uniknąć konfliktu
        await requestMaker.reorderTaskCards(board.id, [
          {
            id: cardId,
            position: 99998 // Tymczasowa pozycja
          },
          {
            id: otherCardId,
            position: 99999 // Tymczasowa pozycja
          }
        ])

        // Następnie ustawiamy docelowe pozycje
        await requestMaker.reorderTaskCards(board.id, [
          {
            id: cardId,
            position: newIndex
          },
          {
            id: otherCardId,
            position: cardIndex
          }
        ])

        // Po udanej zmianie odświeżamy dane z serwera
        fetchBoard()

        dispatch(
          RefreshActions.setToastMessage({
            message: direction === 'up' ? 'Zadanie zostało przesunięte w górę' : 'Zadanie zostało przesunięte w dół'
          })
        )
      } catch (error) {
        console.error('Błąd podczas zmiany pozycji karty', error)
        // Przywracamy poprzedni stan w przypadku błędu
        fetchBoard()

        dispatch(
          RefreshActions.setToastMessage({
            message: 'Nie udało się zmienić pozycji zadania'
          })
        )
      }
    } catch (error) {
      console.error('Błąd podczas przygotowania zmiany pozycji karty', error)
    }
  }

  const handleUpdateCardPriority = async (cardId: string, priority: number) => {
    if (board == null) return

    try {
      await requestMaker.updateTaskCard(board.id, cardId, { priority })
      fetchBoard() // Odśwież dane, aby pokazać zaktualizowaną kartę

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Priorytet zadania został zaktualizowany'
        })
      )
    } catch (error) {
      console.error('Błąd podczas aktualizacji priorytetu karty', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zaktualizować priorytetu zadania'
        })
      )
    }
  }

  const handleAddDependency = async (cardId: string, dependencyData: {
    dependencyType: string
    stageName: string
    customTaskName: string
    customSubtaskName?: string
  }) => {
    if (board == null) return

    try {
      await requestMaker.addTaskCardDependency(board.id, cardId, dependencyData)
      fetchBoard() // Odśwież dane, aby pokazać nowe powiązanie

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Powiązanie zostało dodane'
        })
      )
    } catch (error) {
      console.error('Błąd podczas dodawania powiązania', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się dodać powiązania'
        })
      )
    }
  }

  const handleUpdateDependency = async (cardId: string, dependencyId: string, dependencyData: {
    dependencyType: string
    stageName: string
    customTaskName: string
    customSubtaskName?: string
  }) => {
    if (board == null) return

    try {
      await requestMaker.updateTaskCardDependency(board.id, cardId, dependencyId, dependencyData)
      fetchBoard() // Odśwież dane, aby pokazać zaktualizowane powiązanie

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Powiązanie zostało zaktualizowane'
        })
      )
    } catch (error) {
      console.error('Błąd podczas aktualizacji powiązania', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zaktualizować powiązania'
        })
      )
    }
  }

  const handleRemoveDependency = async (cardId: string, dependencyId: string) => {
    if (board == null) return

    try {
      await requestMaker.removeTaskCardDependency(board.id, cardId, dependencyId)
      fetchBoard() // Odśwież dane, aby usunąć powiązanie

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Powiązanie zostało usunięte'
        })
      )
    } catch (error) {
      console.error('Błąd podczas usuwania powiązania', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się usunąć powiązania'
        })
      )
    }
  }

  const handleAddPredefinedCard = async (columnId: string, stageName: string, taskName: string) => {
    if (board == null) return

    try {
      await requestMaker.createTaskCard(board.id, {
        title: taskName,
        description: `Zadanie: ${taskName}\nEtap: ${stageName}`,
        columnId,
        priority: 0
      })
      fetchBoard()
      setShowAddCardModal(null)
      setSelectedStage('')
      setSelectedTask('')
      setIsPredefineMode(false)

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Karta została dodana'
        })
      )
    } catch (error) {
      console.error('Błąd podczas dodawania karty', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się dodać karty'
        })
      )
    }
  }

  const handleUpdateNotifyOnComplete = async (cardId: string, notifyOnComplete: boolean) => {
    if (!board?.id) return
    try {
      await requestMaker.updateTaskCardNotifyOnComplete(board.id, cardId, notifyOnComplete)
      await fetchBoard()
      dispatch(
        RefreshActions.setToastMessage({
          message: notifyOnComplete ? 'Włączono powiadomienia email' : 'Wyłączono powiadomienia email'
        })
      )
    } catch (error) {
      console.error('Błąd podczas aktualizacji powiadomień:', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zaktualizować powiadomień'
        })
      )
    }
  }

  const handleUpdateCardDueDates = async (cardId: string, dueDate: string | null, dueDateTo: string | null) => {
    if (board == null) return

    try {
      await requestMaker.updateTaskCard(board.id, cardId, { dueDate, dueDateTo })
      fetchBoard() // Odśwież dane, aby pokazać zaktualizowaną kartę

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Termin zadania został zaktualizowany'
        })
      )
    } catch (error) {
      console.error('Błąd podczas aktualizacji terminu', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zaktualizować terminu'
        })
      )
    }
  }

  const handleUpdateColumnTitle = async (columnId: string, title: string) => {
    if (board == null) return

    try {
      await requestMaker.updateTaskColumn(board.id, columnId, title)
      fetchBoard() // Odśwież dane, aby pokazać zaktualizowaną kolumnę

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nazwa kolumny została zaktualizowana'
        })
      )
    } catch (error) {
      console.error('Błąd podczas aktualizacji nazwy kolumny', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zaktualizować nazwy kolumny'
        })
      )
    }
  }

  // Zmiana typów dla funkcji obsługujących zdarzenia
  const handleCardClick = (columnId: string, cardId: string) => {
    setShowCardModal({ columnId, cardId })
  }

  const handlePriorityClick = (columnId: string, cardId: string) => {
    setShowPriorityModal({ columnId, cardId })
  }

  const handleDueDateClick = (columnId: string, cardId: string) => {
    setShowDueDateModal({ columnId, cardId })
  }

  const handleDependencyClick = (columnId: string, cardId: string) => {
    setShowDependencyModal({ columnId, cardId })
  }

  const handleRemoveMember = async (memberId: string) => {
    if (board == null) return

    try {
      await requestMaker.removeTaskBoardMember(board.id, memberId)
      fetchBoard() // Odśwież dane, aby usunąć użytkownika

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Użytkownik został usunięty z tablicy'
        })
      )
    } catch (error) {
      console.error('Błąd podczas usuwania użytkownika', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się usunąć użytkownika'
        })
      )
    }
  }

  if (loading) {
    return (
      <div className='loading-container text-center py-50'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
        <p className='mt-20'>Ładowanie tablicy zadań...</p>
      </div>
    )
  }

  if (board == null) {
    return (
      <div className='text-center py-50'>
        <h3>Nie znaleziono tablicy zadań</h3>
      </div>
    )
  }

  return (
    <>
      <AppNavBar
        centerText={board.title}
        rightIcons={[
          <TaskBoardIcons
            key='task-board-icons'
            viewMode={viewMode}
            filterMode={filterMode}
            board={board}
            userPackage={userPackage}
            packageExpiredAt={packageExpiredAt}
            packageUsersNumber={packageUsersNumber ?? undefined}
            onViewModeChange={setViewMode}
            onFilterModeChange={setFilterMode}
            onInviteClick={() => setShowInviteModal(true)}
            onAddColumnClick={() => setShowAddColumnModal(true)}
          />
        ]}
      />
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 80px)' }}>
        <SideMenu />
        <section className='task-board pt-40 pb-40' style={{ flex: 1, overflow: 'auto' }}>
          <div className='container-fluid'>
            <TaskBoardHeader board={board} />
            <TaskBoardContent
              board={board}
              viewMode={viewMode}
              filterMode={filterMode}
              userState={userState}
              onAddCard={(columnId) => setShowAddCardModal(columnId)}
              onDeleteColumn={handleDeleteColumn}
              onUpdateColumnTitle={handleUpdateColumnTitle}
              onCardClick={handleCardClick}
              onPriorityClick={handlePriorityClick}
              onDueDateClick={handleDueDateClick}
              onDependencyClick={handleDependencyClick}
              onMoveCard={handleMoveCard}
              onChangeCardPosition={handleChangeCardPosition}
              onAssignUser={handleAssignUser}
            />
          </div>
        </section>
      </div>

      <TaskBoardModals
        board={board}
        showInviteModal={showInviteModal}
        showAddColumnModal={showAddColumnModal}
        showAddCardModal={showAddCardModal}
        showCardModal={showCardModal}
        showPriorityModal={showPriorityModal}
        showAssigneeModal={showAssigneeModal}
        showDependencyModal={showDependencyModal}
        showDueDateModal={showDueDateModal}
        inviteEmail={inviteEmail}
        newColumnTitle={newColumnTitle}
        newCard={newCard}
        isPredefineMode={isPredefineMode}
        selectedStage={selectedStage}
        selectedTask={selectedTask}
        dependencies={(() => {
          if (!showDependencyModal|| !board) return []
          const column = board.columns.find(col => col.id === showDependencyModal.columnId)
          if (column == null) return []
          const card = column.cards.find(card => card.id === showDependencyModal.cardId)
          return card?.dependencies || []
        })()}
        packageUsersNumber={packageUsersNumber ?? undefined}
        onInviteModalClose={() => setShowInviteModal(false)}
        onAddColumnModalClose={() => setShowAddColumnModal(false)}
        onAddCardModalClose={() => {
          setShowAddCardModal(null)
          setNewCard({ title: '', description: '', priority: 0 })
          setSelectedStage('')
          setSelectedTask('')
          setIsPredefineMode(false)
        }}
        onCardModalClose={() => setShowCardModal(null)}
        onPriorityModalClose={() => setShowPriorityModal(null)}
        onAssigneeModalClose={() => setShowAssigneeModal(null)}
        onDependencyModalClose={() => setShowDependencyModal(null)}
        onDueDateModalClose={() => setShowDueDateModal(null)}
        onInviteEmailChange={setInviteEmail}
        onNewColumnTitleChange={setNewColumnTitle}
        onNewCardChange={setNewCard}
        onPredefineModeChange={setIsPredefineMode}
        onSelectedStageChange={setSelectedStage}
        onSelectedTaskChange={setSelectedTask}
        onInviteUser={handleInviteUser}
        onRemoveMember={handleRemoveMember}
        onAddColumn={handleAddColumn}
        onAddCard={handleAddCard}
        onAddPredefinedCard={handleAddPredefinedCard}
        onUpdateCardPriority={handleUpdateCardPriority}
        onUpdateCardDueDates={handleUpdateCardDueDates}
        onAssignUser={handleAssignUser}
        onAddDependency={handleAddDependency}
        onUpdateDependency={handleUpdateDependency}
        onRemoveDependency={handleRemoveDependency}
        onAddComment={handleAddComment}
        onDeleteCard={handleDeleteCard}
        onUpdateCardDescription={handleUpdateCardDescription}
        onMoveCard={handleMoveCard}
        onChangeCardPosition={handleChangeCardPosition}
        onUpdateNotifyOnComplete={handleUpdateNotifyOnComplete}
        onDueDateClick={handleDueDateClick}
      />
    </>
  )
}

export default TaskBoard
